import { Component, Host, h, Listen, Element, Prop, State, Method, Watch } from '@stencil/core';
import { BalRegion, useBalConfig } from '@baloise/ds-core';

const config = useBalConfig();
@Component({
  tag: 'bam-field',
})
export class BamField {
  @Element() el: HTMLElement;
  @Prop() isHidden = false;
  @Prop() dontSubmit = false;
  @Prop() disabled: boolean;
  @Prop() readonly: boolean;
  @Prop() invalid: boolean;
  @Prop() loading: boolean;
  @Prop() required = false;
  @Prop() message: string;
  @Prop() messageColor: BalProps.BalFieldMessageColor;
  @Prop({ mutable: true, reflect: true }) errorMessages: string;
  @Prop() iconLeft: string;
  @Prop() iconRight: string;
  @Prop() duplicateInput: string;
  @Prop() showFields?: string;
  @Prop() hideFields?: string;
  @Prop() checkboxGroup = false;
  @State() currentMessage: string;
  @State() currentColor: BalProps.BalFieldMessageColor;
  @State() invalidState: boolean;

  balInput!: HTMLBalInputElement;
  inputElement!: Promise<HTMLInputElement>;
  form: HTMLFormElement;
  errorsMessageObject: {
    valueMissing: string;
    typeMismatch: string;
    patternMismatch: string;
    tooLong: string;
    tooShort: string;
    rangeUnderflow: string;
    rangeOverflow: string;
    stepMismatch: string;
    badInput: string;
    customError: string;
    valid: string;
  };
  inputValid = true;
  newMessage = '';
  inputs: NodeListOf<HTMLInputElement> | NodeListOf<HTMLSelectElement> | NodeListOf<HTMLTextAreaElement>;

  @Watch('errorMessages')
  errorMessagesUpdated() {
    this.errorsMessageObject = JSON.parse(this.errorMessages);
    this.inputs = this.getInputs();

    setTimeout(() => {
      this.validateInput();
    }, 25);
  }

  @Listen('balBlur')
  onBalBlur() {
    setTimeout(() => {
      this.validateInput();
    }, 25);
  }

  @Listen('balChange')
  onBalChange(e: CustomEvent<string>) {
    setTimeout(() => {
      this.validateInput(e);
    }, 25);
  }

  @Listen('paste')
  onPaste(e) {
    if (this.duplicateInput) {
      e.preventDefault();
    }
  }

  @Listen('drop')
  onDrop(e) {
    if (this.duplicateInput) {
      e.preventDefault();
    }
  }

  private getInputs = () =>
    this.el.querySelectorAll('bal-dropdown').length
      ? this.el.querySelectorAll('select')
      : this.el.querySelectorAll('bal-textarea').length
      ? this.el.querySelectorAll('textarea')
      : this.el.querySelectorAll('bal-file-upload').length
      ? this.el.querySelectorAll('textarea')
      : this.el.querySelectorAll('input');

  @Method()
  async switchInputDisabled(value) {
    if (this.dontSubmit) {
      this.inputs.forEach((input: any) => {
        input.setAttribute('disabled', value);
      });
    }
  }

  @Method()
  async validateInput(e: CustomEvent = undefined) {
    this.newMessage = '';
    this.inputValid = true;
    let rangeOverflowCustom = false;
    let rangeUnderflowCustom = false;

    if ((e?.target as HTMLElement)?.tagName === 'BAL-RADIO-GROUP') {
      return;
    }

    if (this.checkboxGroup) {
      this.checkboxGroupValidation();
    } else {
      this.inputs.forEach(input => {
        if (this.duplicateInput) {
          this.checkDuplicateInput(input);
        }
        let inputValidity = input.validity;

        if (input.parentElement.tagName === 'BAL-INPUT-DATE') {
          if (e && e.detail === 'INVALID_VALUE') {
            input.setCustomValidity(this.errorsMessageObject.customError);
          } else if (e && e.detail !== 'INVALID_VALUE') {
            input.setCustomValidity('');
          }
        }

        if ((input.parentElement.tagName === 'BAL-NUMBER-INPUT' || input.parentElement.type === 'number') && !!input.parentElement.max) {
          let parsedInputValue = input.value;
          if (config.region === ('DE' as BalRegion)) {
            parsedInputValue = parseFloat(parsedInputValue.replace(/[.]/g, '').replace(',', '.'));
          } else {
            parsedInputValue = parseFloat(parsedInputValue.replace(/[^\d.-]/g, ''));
          }
          rangeOverflowCustom = parsedInputValue > input.max;
          rangeUnderflowCustom = parsedInputValue < input.min;
        }

        if (!inputValidity.valid || rangeOverflowCustom || rangeUnderflowCustom) {
          this.inputValid = false;
          this.newMessage += this.getValidationMessage(inputValidity, rangeOverflowCustom, rangeUnderflowCustom);
        }
      });
    }
    if (!this.inputValid) {
      this.setError(this.newMessage);
    } else {
      this.cleanError();
    }
  }

  setError(message) {
    this.currentColor = 'danger';
    this.invalidState = true;
    this.currentMessage = message + '<br/>';
  }

  cleanError() {
    this.currentColor = null;
    this.invalidState = false;
    this.currentMessage = this.message ? this.message : '';
  }

  checkboxGroupValidation() {
    let checked = false;

    this.inputs.forEach((input: any) => {
      checked = input.checked ? input.checked : checked;
    });

    if (!checked && this.newMessage.indexOf(this.errorsMessageObject.customError) == -1) {
      this.inputValid = false;
      this.inputs[0].setCustomValidity(this.errorsMessageObject.customError);
      this.newMessage += this.errorsMessageObject.customError + '<br/>';
      this.inputs.forEach((input: any) => {
        input.setAttribute('required', true);
      });
    } else {
      this.inputs[0].setCustomValidity('');
      this.inputValid = true;
      this.inputs.forEach((input: any) => {
        input.removeAttribute('required');
      });
    }
  }

  checkDuplicateInput(input) {
    let inputEl = this.form.querySelector('[name="' + this.duplicateInput + '"] input') as HTMLInputElement;

    if (input.value !== inputEl.value) {
      input.setCustomValidity(this.errorsMessageObject.customError);
      this.newMessage += this.errorsMessageObject.customError + '<br/>';
    } else {
      input.setCustomValidity('');
    }
  }

  getValidationMessage(inputValidity, rangeOverflowCustom, rangeUnderflowCustom) {
    let validationMessage = '';

    if (inputValidity.valueMissing && this.newMessage.indexOf(this.errorsMessageObject.valueMissing) == -1) {
      validationMessage += this.errorsMessageObject.valueMissing + '<br/>';
    }
    if (inputValidity.typeMismatch && this.newMessage.indexOf(this.errorsMessageObject.typeMismatch) == -1) {
      validationMessage += this.errorsMessageObject.typeMismatch + '<br/>';
    }
    if (inputValidity.patternMismatch && this.newMessage.indexOf(this.errorsMessageObject.patternMismatch) == -1) {
      validationMessage += this.errorsMessageObject.patternMismatch + '<br/>';
    }
    if (inputValidity.tooLong && this.newMessage.indexOf(this.errorsMessageObject.tooLong) == -1) {
      validationMessage += this.errorsMessageObject.tooLong + '<br/>';
    }
    if (inputValidity.tooShort && this.newMessage.indexOf(this.errorsMessageObject.tooShort) == -1) {
      validationMessage += this.errorsMessageObject.tooShort + '<br/>';
    }
    if ((inputValidity.rangeUnderflow || rangeUnderflowCustom) && this.newMessage.indexOf(this.errorsMessageObject.rangeUnderflow) == -1) {
      validationMessage += this.errorsMessageObject.rangeUnderflow + '<br/>';
    }
    if ((inputValidity.rangeOverflow || rangeOverflowCustom) && this.newMessage.indexOf(this.errorsMessageObject.rangeOverflow) == -1) {
      validationMessage += this.errorsMessageObject.rangeOverflow + '<br/>';
    }
    if (inputValidity.stepMismatch && this.newMessage.indexOf(this.errorsMessageObject.stepMismatch) == -1) {
      validationMessage += this.errorsMessageObject.stepMismatch + '<br/>';
    }
    if (inputValidity.badInput && this.newMessage.indexOf(this.errorsMessageObject.badInput) == -1) {
      validationMessage += this.errorsMessageObject.badInput + '<br/>';
    }
    if (inputValidity.customError && this.newMessage.indexOf(this.errorsMessageObject.customError) == -1 && this.errorsMessageObject.customError) {
      if (!validationMessage.includes(this.errorsMessageObject.customError)) {
        validationMessage += this.errorsMessageObject.customError + '<br/>';
      }
    }

    return validationMessage;
  }

  componentWillLoad() {
    this.errorsMessageObject = JSON.parse(this.errorMessages);
    this.currentMessage = this.message ? this.message : '';
    this.currentColor = this.messageColor;
    this.invalidState = this.invalid;
  }

  componentDidLoad() {
    if (this.showFields?.length) {
      const rules = this.parseJson(this.showFields);
      if (rules.length) {
        this.initRules(rules);
      }
    }
    this.form = this.el.closest('form');
    this.balInput = this.el.querySelector('bal-input, bal-textarea, bal-radio-group, bal-dropdown, bal-checkbox-group, bal-date') as HTMLBalInputElement;
    this.inputs = this.getInputs();
  }

  initRules(rules) {
    rules.map(rule => {
      if (rule.value) {
        const valueParentSelector = `[id="${rule.name}"]`;
        const valueParentElement: HTMLFormElement = this.el.querySelector(valueParentSelector);
        const valueElementSelector = `[name="${rule.name}"][value="${rule.value}"]`;
        const valueElement: HTMLFormElement = this.el.querySelector(valueElementSelector);
        const valueElementNative: HTMLInputElement = valueElement.querySelector('input');
        const showElement: HTMLElement = document.getElementById(rule.show);
        setTimeout(() => {
          this.checkValue(valueElementNative, showElement);
        }, 50);
        valueParentElement?.addEventListener('balChange', () => {
          setTimeout(() => {
            this.checkValue(valueElementNative, showElement);
          }, 100);
        });
      }
      if (rule.valid) {
        const valueElementSelector = `[name="${rule.name}"]`;
        const valueElement: HTMLFormElement = this.el.querySelector(valueElementSelector);
        const valueElementNative: HTMLInputElement = valueElement.querySelector('input');
        const showElement: HTMLElement = document.getElementById(rule.show);
        setTimeout(() => {
          this.checkValid(valueElementNative, showElement);
        }, 50);
        this.el?.addEventListener('balChange', () => {
          setTimeout(() => {
            this.checkValid(valueElementNative, showElement);
          }, 100);
        });
      }
    });
  }

  checkValid(valueElement, showElement) {
    showElement.classList.toggle('hidden', !valueElement.validity.valid);
  }

  checkValue(valueElement, showElement) {
    showElement.classList.toggle('hidden', !valueElement.checked);
  }

  parseJson(json) {
    try {
      return JSON.parse(json);
    } catch (error) {
      console.error('parseJson', 'error', error);
    }
  }

  render() {
    const { isHidden, disabled, readonly, invalidState, loading, required, currentMessage, currentColor, iconLeft, iconRight } = this;

    return (
      <Host>
        <bal-field disabled={disabled} readonly={readonly} invalid={invalidState} loading={loading} required={required}>
          {!isHidden && <slot name="input-label"></slot>}

          <bal-field-control icon-left={iconLeft} icon-right={iconRight} class={{ 'is-visibly-hidden': isHidden }}>
            <slot></slot>
          </bal-field-control>

          {currentMessage && <bal-field-message color={currentColor} innerHTML={currentMessage}></bal-field-message>}
        </bal-field>
      </Host>
    );
  }
}
